// hooks/useQRGenerator.js - Updated with PDF tracking support
import { useState } from 'react';
import useDynamicQR from './useDynamicQR';

const useQRGenerator = (planTracker, userId) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingError, setProcessingError] = useState('');
  const [generatedQRCodes, setGeneratedQRCodes] = useState([]);
  const [aiResponse, setAiResponse] = useState('');

  const { createDynamicQR } = useDynamicQR(userId);

  // ✅ UPDATED: Added skipStateUpdate and isFromPDF parameters
  const generateQRFromPrompt = async (prompt, isDynamic = false, skipStateUpdate = false, isFromPDF = false) => {
    setIsProcessing(true);
    setProcessingError('');
    setAiResponse('');

    // ✅ UPDATED: Only check limits if NOT from PDF (PDF QRs are unlimited)
    if (!isFromPDF && planTracker) {
      const canGenerate = planTracker.canGenerateQR();
      
      if (!canGenerate) {
        const limitReason = planTracker.getLimitReason();
        const error = limitReason || 'You have reached your QR code generation limit. Please upgrade your plan.';
        setProcessingError(error);
        setIsProcessing(false);
        throw new Error(error);
      }
    }

    // ✅ Check dynamic QR limits (applies to both PDF and regular QRs)
    if (isDynamic && planTracker) {
      const canGenerateDynamic = planTracker.canGenerateDynamicQR();
      
      if (!canGenerateDynamic) {
        const dynamicLimit = planTracker.planConfig?.limits?.dynamicQR || 0;
        const usageStats = planTracker.getUsageStats();
        const dynamicUsed = usageStats?.dynamicQRGenerated || 0;
        
        const error = `You've used all ${dynamicLimit} dynamic QR code${dynamicLimit !== 1 ? 's' : ''} for your ${planTracker.planConfig.name} plan. Upgrade for unlimited dynamic QR codes.`;
        setProcessingError(error);
        setIsProcessing(false);
        throw new Error(error);
      }
    }

    // Check if API key exists
    const apiKey = process.env.REACT_APP_GROQ_API_KEY;
    if (!apiKey) {
      const error = 'Groq API key is missing. Please add REACT_APP_GROQ_API_KEY to your .env file';
      setProcessingError(error);
      setIsProcessing(false);
      throw new Error(error);
    }

    try {
      // Enhanced system prompt with detailed QR code format instructions
      const systemPrompt = `You are an expert QR code content generator. Analyze the user's request and generate properly formatted QR code content.

IMPORTANT FORMATS TO USE:

1. VCARD (Contact Cards):
Format: BEGIN:VCARD
VERSION:3.0
FN:Full Name
TEL:+1234567890
EMAIL:email@example.com
ORG:Company Name
TITLE:Job Title
URL:https://website.com
ADR:;;Street;City;State;ZIP;Country
NOTE:Additional notes
END:VCARD

2. WIFI Networks:
Format: WIFI:T:WPA;S:NetworkName;P:Password;;
- T can be: WPA, WEP, or nopass (for open networks)
- S is the network SSID
- P is the password (omit for open networks)
- H:true for hidden networks

3. URLs & Links:
- Always use full URLs with https://
- Social media: https://instagram.com/username, https://twitter.com/handle
- YouTube: https://youtube.com/watch?v=VIDEO_ID
- Google Maps: https://maps.google.com/?q=latitude,longitude

4. EMAIL:
Format: mailto:email@example.com?subject=Subject&body=Body text

5. PHONE:
Format: tel:+1234567890
Use E.164 format with country code

6. SMS:
Format: smsto:+1234567890:Message text here

7. WHATSAPP:
Format: https://wa.me/1234567890?text=Your%20message

8. PAYMENT LINKS:
- PayPal: https://paypal.me/username/amount
- Venmo: venmo://paycharge?recipients=username&amount=20&note=description
- Cash App: https://cash.app/$username/amount

9. CALENDAR EVENTS (vEvent):
Format: BEGIN:VEVENT
SUMMARY:Event Title
DTSTART:20240101T120000Z
DTEND:20240101T130000Z
LOCATION:Event Location
DESCRIPTION:Event Description
END:VEVENT

10. CRYPTOCURRENCY:
- Bitcoin: bitcoin:address?amount=0.001&label=Description
- Ethereum: ethereum:address@chainId?value=amount

11. APP DEEP LINKS:
- Spotify: spotify:track:TRACK_ID
- YouTube: vnd.youtube://VIDEO_ID
- Telegram: https://t.me/username

12. GEOLOCATION:
Format: geo:latitude,longitude?q=latitude,longitude(Label)
Example: geo:37.7749,-122.4194?q=37.7749,-122.4194(San Francisco)

INSTRUCTIONS:
- Detect the intent from the user's natural language
- Return ONLY the formatted content, no explanations
- Use proper escaping and encoding where needed
- For ambiguous requests, choose the most practical format
- Always validate phone numbers, emails, and URLs
- Include all necessary fields for the format

Examples:
User: "Add John Doe, phone 555-1234, email john@example.com"
Output: BEGIN:VCARD
VERSION:3.0
FN:John Doe
TEL:+15551234
EMAIL:john@example.com
END:VCARD

User: "WiFi network HomeNet password MyPass123"
Output: WIFI:T:WPA;S:HomeNet;P:MyPass123;;

User: "My Instagram is @cooluser"
Output: https://instagram.com/cooluser`;

      // Call Groq API to process the prompt
      const response = await fetch('https://api.groq.com/openai/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${apiKey}`
        },
        body: JSON.stringify({
          model: "llama-3.3-70b-versatile",
          messages: [
            {
              role: "system",
              content: systemPrompt
            },
            {
              role: "user",
              content: prompt
            }
          ],
          temperature: 0.3,
          max_tokens: 1000
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error?.message || 'Failed to process prompt with AI');
      }

      const data = await response.json();
      const aiGeneratedContent = data.choices[0]?.message?.content?.trim();

      if (!aiGeneratedContent) {
        throw new Error('No content generated from AI');
      }

      // Detect QR code type for better UI display
      const qrType = detectQRType(aiGeneratedContent);

      // ✅ Validate that dynamic QR is URL-based
      if (isDynamic) {
        // Only allow URL-based types for dynamic QR
        const allowedDynamicTypes = ['url', 'instagram', 'twitter', 'youtube', 'whatsapp', 'payment', 'maps'];
        
        if (!allowedDynamicTypes.includes(qrType)) {
          const typeNames = {
            'vcard': 'Contact Card (vCard)',
            'wifi': 'WiFi Network',
            'email': 'Email',
            'phone': 'Phone Number',
            'sms': 'SMS',
            'event': 'Calendar Event',
            'location': 'Location',
            'crypto': 'Cryptocurrency',
            'app': 'App Link',
            'text': 'Plain Text'
          };
          
          const friendlyTypeName = typeNames[qrType] || qrType;
          
          throw new Error(
            `⚠️ Dynamic QR codes only work with website links and social media URLs.\n\n` +
            `Your content is a "${friendlyTypeName}" which cannot be made dynamic because browsers can only redirect to URLs.\n\n` +
            `💡 Solution: Disable the "Dynamic" toggle to create a static QR code, or use a URL-based prompt instead.`
          );
        }
      }

      // ✅ UPDATED: Consume credit with isFromPDF flag
      // If isFromPDF = true, this won't consume regular QR credits (unlimited QR from PDF)
      if (planTracker) {
        try {
          const consumed = await planTracker.consumeCredit(isDynamic, isFromPDF);
          if (!consumed) {
            throw new Error('Failed to track QR code generation. Please try again.');
          }
        } catch (consumeError) {
          console.error('Error consuming credit:', consumeError);
          throw new Error('Failed to track usage. Please try again.');
        }
      }

      setAiResponse(aiGeneratedContent);

      let newQRCode;

      // Create dynamic QR if requested
      if (isDynamic && userId) {
        try {
          const dynamicQR = await createDynamicQR({
            qrContent: aiGeneratedContent,
            qrType: qrType,
            originalPrompt: prompt,
            title: prompt.substring(0, 50),
            size: 256,
            bgColor: '#ffffff',
            fgColor: '#000000',
            level: 'M'
          });

          newQRCode = {
            ...dynamicQR,
            isDynamic: true,
            timestamp: new Date().toISOString(),
            isFromPDF: isFromPDF // ✅ Track if from PDF
          };
        } catch (dynamicError) {
          console.error('Error creating dynamic QR:', dynamicError);
          throw new Error('Failed to create dynamic QR code. ' + dynamicError.message);
        }
      } else {
        // Create static QR code
        newQRCode = {
          id: Date.now(),
          originalPrompt: prompt,
          qrContent: aiGeneratedContent,
          qrType: qrType,
          timestamp: new Date().toISOString(),
          size: 256,
          bgColor: '#ffffff',
          fgColor: '#000000',
          level: 'M',
          isDynamic: false,
          isFromPDF: isFromPDF // ✅ Track if from PDF
        };
      }

      // ✅ UPDATED: Only add to state if skipStateUpdate is false
      if (!skipStateUpdate) {
        setGeneratedQRCodes(prev => [newQRCode, ...prev]);
      }
      
      return newQRCode;
    } catch (error) {
      console.error('Error generating QR code:', error);
      setProcessingError(error.message || 'Failed to generate QR code');
      throw error;
    } finally {
      setIsProcessing(false);
    }
  };

  // Helper function to detect QR code type
  const detectQRType = (content) => {
    if (content.startsWith('BEGIN:VCARD')) return 'vcard';
    if (content.startsWith('WIFI:')) return 'wifi';
    if (content.startsWith('BEGIN:VEVENT')) return 'event';
    if (content.startsWith('mailto:')) return 'email';
    if (content.startsWith('tel:')) return 'phone';
    if (content.startsWith('smsto:')) return 'sms';
    if (content.startsWith('geo:')) return 'location';
    if (content.startsWith('http://') || content.startsWith('https://')) {
      if (content.includes('instagram.com')) return 'instagram';
      if (content.includes('twitter.com') || content.includes('x.com')) return 'twitter';
      if (content.includes('youtube.com')) return 'youtube';
      if (content.includes('wa.me')) return 'whatsapp';
      if (content.includes('paypal.me') || content.includes('cash.app')) return 'payment';
      if (content.includes('maps.google.com')) return 'maps';
      return 'url';
    }
    if (content.startsWith('bitcoin:') || content.startsWith('ethereum:')) return 'crypto';
    if (content.startsWith('spotify:') || content.includes('vnd.youtube')) return 'app';
    return 'text';
  };

  const updateQRCode = (id, updates) => {
    setGeneratedQRCodes(prev =>
      prev.map(qr => qr.id === id ? { ...qr, ...updates } : qr)
    );
  };

  const deleteQRCode = (id) => {
    setGeneratedQRCodes(prev => prev.filter(qr => qr.id !== id));
  };

  const clearAllQRCodes = () => {
    setGeneratedQRCodes([]);
    setAiResponse('');
    setProcessingError('');
  };

  return {
    isProcessing,
    processingError,
    generatedQRCodes,
    aiResponse,
    generateQRFromPrompt,
    updateQRCode,
    deleteQRCode,
    clearAllQRCodes,
    setGeneratedQRCodes 
  };
};

export default useQRGenerator;